// PHASE_0 B0 deliverable 2 — application-composite empty-state primitive.
//
// Rendered when a list/section resolved successfully but has zero rows
// (distinct from Loading = in-flight and ErrorState = failed). Content is
// caller-supplied (title/description/action) so the host owns i18n keys —
// the component owns layout + ARIA, never literal copy.

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";

export interface EmptyProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Optional decorative icon/illustration slot. */
  readonly icon?: ReactNode;
  /** Short headline (already-localized text or node). */
  readonly title: ReactNode;
  /** Optional supporting copy. */
  readonly description?: ReactNode;
  /** Optional primary action (e.g. a <Button>Create</Button>). */
  readonly action?: ReactNode;
}

export const Empty = forwardRef<HTMLDivElement, EmptyProps>(
  ({ icon, title, description, action, className, ...rest }, ref) => (
    <div
      ref={ref}
      data-component="empty"
      className={cx("nai-empty", className)}
      {...rest}
    >
      {icon !== undefined && (
        <div data-slot="empty-icon" aria-hidden="true">
          {icon}
        </div>
      )}
      <p data-slot="empty-title" className="nai-empty-title">
        {title}
      </p>
      {description !== undefined && (
        <p data-slot="empty-description" className="nai-empty-description">
          {description}
        </p>
      )}
      {action !== undefined && <div data-slot="empty-action">{action}</div>}
    </div>
  ),
);
Empty.displayName = "Empty";
